import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import {
  loadStripe,
  PaymentIntent,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import { useLocation } from "react-router-dom";
import Axios from "axios";
import { useAppSelector } from "store/hooks";
import { useDispatch } from "react-redux";

import DonationNavBar from "../../components/layout/navigation/DonationNavBar/DonationNavBar";
import DonationNavBarMobile from "components/layout/navigation/DonationNavBarMobile/DonationNavBarMobile";
import DonationForm from "./DonationForm/DonationForm";
import DonationFormSkeleton from "./DonationForm/DonationFormSkeleton";
import ErrorModal from "./ErrorModal/ErrorModal";

import { updateUser } from "store/user/user.slice";
import { updateDonorsList } from "store/ui/donorsList/donorsList.slice";
import { createPaymentIntent } from "apis/PaymentServiceApi";
import { CampaignInfo } from "types";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const stripeKey =
  process.env.REACT_APP_STRIPE_PUBLISH_LIVE ||
  process.env.REACT_APP_STRIPE_PUBLISH_TEST;
if (!stripeKey) throw new Error("Missing Stripe publishable key");

const stripePromise = loadStripe(stripeKey);

export const DonateFlow = () => {
  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user);
  const showErrorPopup = useAppSelector((state) => state.error.showErrorPopup);

  const [isMobile, setIsMobile] = useState(false);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo>({
    awards: "",
    created_at: "",
    default_image: "",
    description: "",
    goal: 0,
    id: "",
    location: "",
    name: "",
    short_id: "",
    shortened_location: "",
    status: "",
    total_raised: 0,
    updated_at: "",
    user_id: 0,
    count_donor: 0,
    project_id: 0,
    start_date_time: "",
    end_date_time: "",
  });
  const [eventInfo, setEventInfo] = useState<EventInfo>();
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent>();

  const query = useQuery();
  const userId = query.get("userId");
  const campaignShort = query.get("campaignShort");
  const activityId = query.get("activityId");
  const rawAmount = parseInt(query.get("amount") ?? "0");
  const amount = (rawAmount / 100).toFixed(2);

  useEffect(() => {
    const fetchData = async () => {
      setIsMobile(/Mobi/i.test(window.navigator.userAgent));
      try {
        const { data } = await Axios.post(
          `${process.env.REACT_APP_API_URL}/auth/securityHole`,
          { userId }
        );
        const token = data.accessToken;

        const app = Axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: { Authorization: `Bearer ${token}` },
        });

        const [userRes, campaignRes, donorsRes, eventRes] =
          await Promise.allSettled([
            app.post("/user/retrieveDocument", { id: userId }),
            app.post("/campaign/retrieveDocument", {
              campaign_short: campaignShort,
            }),
            app.post("/donor/retrieveCampaignDonors", {
              campaign_short: campaignShort,
            }),
            app.post("/activity/retrieveDocument", { id: activityId }),
          ]);

        dispatch(
          updateUser(userRes.status === "fulfilled" ? userRes.value.data : {})
        );
        setCampaignInfo(
          campaignRes.status === "fulfilled" ? campaignRes.value.data : {}
        );
        dispatch(
          updateDonorsList(
            donorsRes.status === "fulfilled" ? donorsRes.value.data : []
          )
        );
        setEventInfo(
          eventRes.status === "fulfilled" ? eventRes.value.data : {}
        );

        const paymentIntent: PaymentIntent = await createPaymentIntent(
          rawAmount,
          "usd",
          {
            userId: userId ?? "",
            campaignShort: campaignShort ?? "",
            activityId: activityId ?? "",
          }
        );

        console.log(paymentIntent);

        setPaymentIntent(paymentIntent);
      } catch (error) {
        console.error("Error fetching donation data", error);
      }
    };

    fetchData();
  }, [userId, campaignShort, activityId, rawAmount, dispatch]);

  const options: StripeElementsOptions = {
    clientSecret: paymentIntent?.client_secret || undefined,
    appearance: {
      theme: "none",
      variables: {
        colorText: "#2E3A59",
        colorPrimary: "#FF2D55",
        spacingTab: "10px",
        borderRadius: "5px",
        colorIcon: "#007AFF",
        colorIconTabHover: "#007AFF",
        colorIconTabSelected: "#007AFF",
      },
      rules: {
        ".Tab": {
          backgroundColor: "#F5F9FF",
          boxShadow:
            "0px 2px 2px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)",
        },
        ".Tab:hover, .Tab--selected": {
          outline: "#007AFF 3px solid",
        },
        ".Input": {
          backgroundColor: "#F7F7F7",
          border: "#E5E5E5 1px solid",
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)",
        },
      },
    },
  };

  return (
    <div style={{ width: "90vw", margin: "2rem auto" }}>
      {isMobile ? <DonationNavBarMobile /> : <DonationNavBar />}
      {paymentIntent?.client_secret ? (
        <Elements stripe={stripePromise} options={options}>
          <DonationForm
            userInfo={user}
            amount={amount}
            campaignInfo={campaignInfo}
            eventInfo={eventInfo}
            isMobile={isMobile}
          />
        </Elements>
      ) : (
        <DonationFormSkeleton />
      )}
      {showErrorPopup && <ErrorModal />}
    </div>
  );
};
